import { getOnChainTools } from '@goat-sdk/adapter-vercel-ai';
import { coingecko } from '@goat-sdk/plugin-coingecko';
import { modespray } from '@goat-sdk/plugin-modespray';
import { zeroDevGlobalAddress } from '@goat-sdk/plugin-zerodev-global-address';
import type { ToolSet } from 'ai';

import OrderlyAPIClient from 'mode-shared/clients/OrderlyAPIClient';
import WagmiWalletClient from 'mode-shared/clients/WagmiWalletClient';
import { goatERC20Tokens as tokens } from 'mode-shared/configs';
import { modeGovernance } from 'mode-shared/plugins/mode-governance';
import { modeTrade } from 'mode-shared/plugins/mode-trade';
import { velodrome } from 'mode-shared/plugins/velodrome';
import { hedgey } from 'mode-shared/plugins/hedgey';
import { erc20 } from 'mode-shared/plugins/erc20';
import { lifi } from 'mode-shared/plugins/lifi';
import type { CommonParameters } from 'mode-shared/types/common';
import type { InitializeGOATParameters } from 'mode-shared/types/goat';
import { createMathTools } from 'mode-shared/tools/math';
import { createModeAPYTool } from 'mode-shared/tools/ModeAPY';
import { createCurrentDateTimeTool } from 'mode-shared/tools';

/**
 * Convenience function that creates the GOAT tools.
 * @param {InitializeGOATParameters} parameters - Adds keys, wallet configs and whether the tools are destined for client/server side use.
 */
export async function initializeGOATTools(parameters: InitializeGOATParameters): Promise<ToolSet> {
  const commonPluginParameters: CommonParameters = {
    logger: parameters.logger,
  };

  let tools: ToolSet = {
    // utility tools
    ...createMathTools(),
    mode_gov_apy: createModeAPYTool(),
    getCurrentDateTime: createCurrentDateTimeTool(commonPluginParameters),
  };

  tools = {
    ...tools,
    ...((await getOnChainTools({
      wallet: new WagmiWalletClient(parameters.wagmiConfig),
      plugins: [
        erc20({
          tokens,
        }),
        modeGovernance(commonPluginParameters), // staking/voting
        modespray(),
        velodrome(), // swaps
        zeroDevGlobalAddress(),
        modeTrade({
          ...commonPluginParameters,
          client: new OrderlyAPIClient({
            baseURL: process.env.NEXT_PUBLIC_ORDERLY_API_URL || 'https://api.orderly.org',
            brokerID: process.env.NEXT_PUBLIC_ORDERLY_BROKER_ID || 'mode',
            logger: parameters.logger,
          }),
        }), // trading
        hedgey(),
        lifi(), // bridging
        ...(!parameters.client ? [coingecko({ apiKey: parameters.coinCategoriesAPIKey, isPro: true })] : []),
      ],
    })) as ToolSet),
  };

  // if this is being used on the server side, we need to remove the execute function for each tool that is to be
  // invoked on the client side, so that they are passed onto the onToolCall
  if (!parameters.client) {
    Object.keys(tools).map((name) => {
      if (!name.startsWith('coingecko_')) {
        delete tools[name].execute;
      }
    });
  }

  // remove tool that doesn't work well
  delete tools['coingecko_get_coin_price_by_contract_address'];
  delete tools['coingecko_get_token_data_by_token_address'];

  return tools;
}
